import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  Backdrop,
  Modal,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  Error as ErrorIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface ImageMessageProps {
  imageUrl: string;
  imagePath?: string;
  imageFilename?: string;
  imageSize?: number;
  imageWidth?: number;
  imageHeight?: number;
  imageAltText?: string;
  isOwnMessage: boolean;
  timestamp: string;
  senderName?: string;
}

const ImageMessage: React.FC<ImageMessageProps> = ({
  imageUrl,
  imagePath,
  imageFilename,
  imageSize,
  imageWidth,
  imageHeight,
  imageAltText,
  isOwnMessage,
  timestamp,
  senderName,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Calculate display dimensions while maintaining aspect ratio
  const maxWidth = 300;
  const maxHeight = 400;

  useEffect(() => {
    if (imageWidth && imageHeight) {
      const aspectRatio = imageWidth / imageHeight;
      let displayWidth = maxWidth;
      let displayHeight = maxWidth / aspectRatio;

      if (displayHeight > maxHeight) {
        displayHeight = maxHeight;
        displayWidth = maxHeight * aspectRatio;
      }

      setImageDimensions({ width: displayWidth, height: displayHeight });
    }
  }, [imageWidth, imageHeight]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = imageFilename || 'image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          display: 'flex',
          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
          marginBottom: '8px',
        }}
      >
        <Paper
          elevation={2}
          sx={{
            maxWidth: maxWidth,
            borderRadius: 3,
            overflow: 'hidden',
            background: isOwnMessage 
              ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
              : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
            position: 'relative',
            ...(imageDimensions && {
              width: imageDimensions.width,
              height: imageDimensions.height,
            }),
          }}
        >
          {/* Image Container */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          >
            {/* Loading State */}
            {!imageLoaded && !imageError && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  p: 3,
                }}
              >
                <CircularProgress size={40} sx={{ color: 'white' }} />
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.8 }}>
                  Loading image...
                </Typography>
              </Box>
            )}

            {/* Error State */}
            {imageError && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  p: 3,
                }}
              >
                <ErrorIcon sx={{ fontSize: 40, color: 'white', opacity: 0.7 }} />
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.8 }}>
                  Failed to load image
                </Typography>
              </Box>
            )}

            {/* Image */}
            {!imageError && (
              <img
                ref={imgRef}
                src={imageUrl}
                alt={imageAltText || 'Shared image'}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: imageLoaded ? 'block' : 'none',
                }}
              />
            )}

            {/* Overlay Actions */}
            {imageLoaded && (
              <Fade in={true}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1,
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  <Tooltip title="View fullscreen">
                    <IconButton
                      size="small"
                      onClick={() => setFullscreenOpen(true)}
                      sx={{
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(0, 0, 0, 0.8)',
                        },
                      }}
                    >
                      <FullscreenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={handleDownload}
                      sx={{
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(0, 0, 0, 0.8)',
                        },
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Image Info Footer */}
          {imageLoaded && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                p: 1,
                opacity: 0,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontSize: '0.7rem',
                  display: 'block',
                }}
              >
                {imageFilename && imageSize && `${imageFilename} • ${formatFileSize(imageSize)}`}
              </Typography>
              {imageAltText && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontSize: '0.7rem',
                    display: 'block',
                    opacity: 0.8,
                  }}
                >
                  {imageAltText}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>

      {/* Fullscreen Modal */}
      <Modal
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { background: 'rgba(0, 0, 0, 0.9)' },
        }}
      >
        <Fade in={fullscreenOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              outline: 'none',
            }}
          >
            <img
              src={imageUrl}
              alt={imageAltText || 'Shared image'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
            {imageAltText && (
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  mt: 2,
                  textAlign: 'center',
                  background: 'rgba(0, 0, 0, 0.7)',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {imageAltText}
              </Typography>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default ImageMessage;
